import type {
  DealDetail,
  DealGenerateRoute,
  DealLineItem,
  DealRevenueMode,
  GenerateDealRequest,
} from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D8 · the wizard's arithmetic, kept off the screen. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The draft the four steps edit, the step order, the request it becomes, and the preview of what
 * the server will make. ⚠️ The preview mirrors `deal-generate.service.ts` rule for rule — a line
 * item folds into its milestone, tasks need a carried line item, bookings need a project AND a
 * carried line item — so the summary never promises a row the server will not write.
 */

export type ContractType = 'milestone' | 'service';
/** The `contract` route's anchor: an existing project, or the client alone. */
export type GenerateTarget = 'project' | 'none';

export interface GenerateDraft {
  readonly route: DealGenerateRoute;
  readonly target: GenerateTarget;
  readonly existingProjectId: string;
  readonly projectName: string;
  readonly projectCode: string;
  readonly pmMemberId: string;
  readonly contractType: ContractType;
  readonly contractName: string;
  readonly lineItemIds: ReadonlySet<string>;
  readonly milestonesAsLineItems: boolean;
  readonly milestonesAsTasks: boolean;
  /** Placeholder → member; `''` is unmatched, and an unmatched placeholder gets no booking. */
  readonly matches: Readonly<Record<string, string>>;
}

/**
 * ⚠️ The server takes `milestone` and `service` only — a recurring deal still becomes a service
 * contract here, where the prototype offered *Ismétlődő* as a third type.
 */
export function contractTypeFor(mode: DealRevenueMode): ContractType {
  return mode === 'milestones' || mode === 'one_off' ? 'milestone' : 'service';
}

export function initialDraft(deal: DealDetail): GenerateDraft {
  return {
    route: 'both',
    target: 'none',
    existingProjectId: '',
    projectName: deal.name,
    projectCode: '',
    pmMemberId: deal.ownerMemberId,
    contractType: contractTypeFor(deal.revenueMode),
    contractName: deal.name,
    /* Options are never counted, so they start unticked — the prototype's `off`. */
    lineItemIds: new Set(deal.lineItems.filter((l) => l.type !== 'option').map((l) => l.id)),
    milestonesAsLineItems: deal.revenueMode === 'milestones',
    milestonesAsTasks: deal.revenueMode === 'milestones',
    /* A placeholder that already names a person — a tentative booking — defaults to them. */
    matches: Object.fromEntries(deal.placeholders.map((p) => [p.id, p.memberId ?? ''])),
  };
}

/** Whether a project exists once this runs — created, or the existing one the contract joins. */
export function hasProject(draft: GenerateDraft): boolean {
  if (draft.route !== 'contract') return true;
  return draft.target === 'project' && draft.existingProjectId !== '';
}

export function nextStep(step: number, draft: GenerateDraft): number {
  if (step === 0) return draft.route === 'contract' && draft.target === 'none' ? 2 : 1;
  if (step === 1) return draft.route === 'project' ? 3 : 2;
  return 3;
}

export function previousStep(step: number, draft: GenerateDraft): number {
  if (step === 3) return draft.route === 'project' ? 1 : 2;
  if (step === 2) return draft.route === 'contract' && draft.target === 'none' ? 0 : 1;
  return 0;
}

/** The first step whose fields are incomplete, or null. */
export function invalidStep(draft: GenerateDraft): { step: number; which: Problem } | null {
  if (draft.route !== 'contract') {
    if (draft.projectName.trim() === '' || draft.pmMemberId === '') {
      return { step: 1, which: 'project' };
    }
  } else if (draft.target === 'project' && draft.existingProjectId === '') {
    return { step: 1, which: 'existing' };
  }
  if (draft.route !== 'project' && draft.contractName.trim() === '') {
    return { step: 2, which: 'contract' };
  }
  return null;
}
export type Problem = 'project' | 'existing' | 'contract';

/** The lines this draft carries, in the plan's own order. */
function chosenLines(deal: DealDetail, draft: GenerateDraft): DealLineItem[] {
  return deal.lineItems.filter((l) => draft.lineItemIds.has(l.id));
}

export function toRequest(deal: DealDetail, draft: GenerateDraft): GenerateDealRequest {
  const withProject = hasProject(draft);
  const carries = draft.route !== 'project';
  return {
    route: draft.route,
    project:
      draft.route === 'contract'
        ? null
        : {
            name: draft.projectName.trim(),
            code: draft.projectCode.trim() === '' ? null : draft.projectCode.trim(),
            pmMemberId: draft.pmMemberId,
          },
    existingProjectId:
      draft.route === 'contract' && draft.target === 'project' && draft.existingProjectId !== ''
        ? draft.existingProjectId
        : null,
    contract: carries ? { type: draft.contractType, name: draft.contractName.trim() } : null,
    /* ⚠️ Sent on the project route too: the server dates the project from these lines. */
    lineItemIds: chosenLines(deal, draft).map((l) => l.id),
    milestonesAsLineItems: carries && draft.milestonesAsLineItems,
    milestonesAsTasks: carries && withProject && draft.milestonesAsTasks,
    matches:
      carries && withProject
        ? Object.fromEntries(Object.entries(draft.matches).filter(([, member]) => member !== ''))
        : {},
  };
}

/** What a deal line item becomes: skipped, folded into its milestone, or one of its own. */
export type LineTarget =
  | { readonly kind: 'skipped' }
  | { readonly kind: 'folded'; readonly milestone: string }
  | { readonly kind: 'fixed' | 'timeAndMaterial' | 'license' };

export function lineTarget(line: DealLineItem, draft: GenerateDraft): LineTarget {
  if (!draft.lineItemIds.has(line.id)) return { kind: 'skipped' };
  if (draft.milestonesAsLineItems && line.milestoneId !== null) {
    return { kind: 'folded', milestone: line.milestoneName ?? line.name };
  }
  if (line.type === 'tm') return { kind: 'timeAndMaterial' };
  if (line.type === 'license') return { kind: 'license' };
  /* An option, ticked, becomes fixed — the service's `TYPE_OF`. */
  return { kind: 'fixed' };
}

export interface GeneratePreview {
  readonly project: 'new' | 'existing' | 'none';
  readonly contract: boolean;
  readonly lineItems: number;
  readonly tasks: number;
  /** Distinct people who will receive at least one booking. */
  readonly people: number;
  readonly hours: number;
  readonly revenue: number;
  readonly estimate: number;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
}

export function previewOf(deal: DealDetail, draft: GenerateDraft): GeneratePreview {
  const chosen = chosenLines(deal, draft);
  const carries = draft.route !== 'project';
  const withProject = hasProject(draft);

  let lineItems = 0;
  if (carries) {
    const milestones = new Set<string>();
    for (const line of chosen) {
      if (draft.milestonesAsLineItems && line.milestoneId !== null)
        milestones.add(line.milestoneId);
      else lineItems += 1;
    }
    lineItems += milestones.size;
  }

  const carried = new Set(carries ? chosen.map((l) => l.id) : []);
  const cells = withProject
    ? deal.allocations.filter(
        (c) => carried.has(c.lineItemId) && (draft.matches[c.placeholderId] ?? '') !== '',
      )
    : [];
  const people = new Set(cells.map((c) => draft.matches[c.placeholderId]));

  const dueOf = new Map(deal.milestones.map((m) => [m.id, m.dueOn]));
  const starts = chosen
    .map((l) => l.startsOn)
    .filter((d): d is string => d !== null)
    .sort();
  const ends = chosen
    .map((l) => l.endsOn ?? (l.milestoneId === null ? null : (dueOf.get(l.milestoneId) ?? null)))
    .filter((d): d is string => d !== null)
    .sort();

  return {
    project: draft.route !== 'contract' ? 'new' : withProject ? 'existing' : 'none',
    contract: carries,
    lineItems,
    tasks:
      draft.milestonesAsTasks && carries && withProject && lineItems > 0
        ? deal.milestones.length
        : 0,
    people: people.size,
    hours: cells.reduce((sum, c) => sum + Number(c.hours), 0),
    revenue: chosen.reduce((sum, l) => sum + Number(l.amount), 0),
    estimate: chosen.reduce((sum, l) => sum + Number(l.estimateHours), 0),
    startsOn: starts[0] ?? null,
    endsOn: ends.at(-1) ?? null,
  };
}
